/* eslint-disable react/jsx-props-no-spreading */
import React, {
  forwardRef, useContext, useEffect, useMemo,
} from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog, DialogContent, DialogTitle, Grid, IconButton, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { TypesDialogBox } from '../../../../utils/interface';
import { LocalizationContext } from '../../../../Common/Localization/LocalizationProvider';
import { AddTypeListData, TypeList } from '../utils/types';
import ComponentTextInput from '../../../../Common/ComponentTextinput/ComponentTextInput';
import { TypeListFormValue } from '../utils/ConstantsTypeList';
import { RootState } from '../../../../redux/rootState';
import { typeListSchema } from '../utils/validations';
import {
  addTypeListStart, resetState, updateTypeList,
} from '../redux/sliceTypeList';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Zoom ref={ref} {...props} />);

function ComponentTypeListDialog({
  show, setOpenFrom, rowId, handleData,
}: TypesDialogBox): JSX.Element {
  const { translations } = useContext(LocalizationContext);
  const { isSuccess, TypeListData } = useSelector((state: RootState) => state.ListTypeReducer);

  const dispatch = useDispatch();

  const {
    formState: { errors }, control, handleSubmit, setValue, reset,
  } = useForm<AddTypeListData>({
    resolver: yupResolver(typeListSchema as ObjectSchema<AddTypeListData>),
  });

  const resetValues = () => {
    setValue(TypeListFormValue.DESCRIPTION, '');
  };

  const onSubmit = (data: AddTypeListData) => {
    if (rowId) {
      const updateData = {
        Id: rowId,
        Description: data.Description,
      };
      dispatch(updateTypeList(updateData));
      setOpenFrom();
      resetValues();
    } else {
      const bodyData = {
        Description: data.Description,
      };
      dispatch(addTypeListStart(bodyData));
      setOpenFrom();
      resetValues();
    }
  };

  const tableData: TypeList[] = useMemo(() => TypeListData?.Data?.map((item, index) => {
    const transformedDataItem = {
      sn: index + 1,
      action: 'edit&delete',
      Description: item.Description,
      id: String(item?.Id),
    };
    return transformedDataItem;
  }), [TypeListData]);

  useEffect(() => {
    if (rowId) {
      tableData?.forEach((rowData) => {
        if (rowData?.id === rowId) {
          setValue(TypeListFormValue.DESCRIPTION, rowData.Description);
        }
      });
    }
  }, [rowId, show]);

  useEffect(() => {
    if (isSuccess) {
      resetValues();
      if (handleData) {
        handleData();
      }
      dispatch(resetState());
    }
  }, [isSuccess]);

  const handleClose = () => {
    setOpenFrom();
    reset();
    setValue(TypeListFormValue.DESCRIPTION, '');
  };

  const getAnalystHeading = () => {
    if (rowId) {
      return 'Edit Description';
    }
    return 'Add Description';
  };

  return (
    <div>
      <Dialog
        maxWidth="sm"
        PaperProps={{
          className: 'paperStyle',
        }}
        open={show}
        transitionDuration={500}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="modalHeaderTitle">
          {getAnalystHeading()}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">{translations.description}</label>
                  <ComponentTextInput
                    control={control}
                    className="nonCapitalizeField"
                    inputProps={{ maxLength: 50 }}
                    name={TypeListFormValue.DESCRIPTION}
                    size="small"
                    id={TypeListFormValue.DESCRIPTION}
                    errors={errors}
                  />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="save_and_next_div">
                  <div>
                    <Button className="button_cancel" onClick={handleClose}>{translations.cancel}</Button>
                  </div>
                  <div className="button_margin_left">
                    <Button type="submit" className="button_save_and_next">{translations.save}</Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ComponentTypeListDialog;
